import React, { useCallback } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import Image from '@atlaskit/image';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import Radio from '@atlaskit/radio/radio';
import { token } from '@atlaskit/tokens';

import { PreviewSize, type PreviewSizeProps, type PreviewSizeVariant } from '../preview-size';

/**
 * Heading size depending on layout variant
 */
const headingSizeMap = {
	default: 'xsmall',
	compact: 'xxsmall',
} satisfies Record<NonNullable<PreviewSizeVariant>, 'xsmall' | 'xxsmall'>;

/**
 * Mapping layout variants to classnames
 */
const variantMap = {
	default: 'defaultLayoutOption',
	compact: 'compactLayoutOption',
} satisfies Record<NonNullable<PreviewSizeVariant>, string>;

const layoutOptionStyles = cssMap({
	defaultLayoutOption: {
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.small'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		'&:hover': {
			cursor: 'pointer',
		},
	},
	compactLayoutOption: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.075'),
		paddingRight: token('space.075'),
	},
	layoutOptionSelected: {
		borderColor: token('color.border.selected'),
	},
	radioButtonWrapper: {
		marginLeft: token('space.negative.050'),
		alignItems: 'center',
	},
	layoutLabelHeadingWrapper: {
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		marginLeft: token('space.negative.025'),
		marginTop: token('space.025'),
	},
});

export type ImageProps = {
	altText?: string;
	imageSrc?: string;
	imageSrcDark?: string;
};

type LayoutOptionsType<TLayout> = {
	description?: MessageDescriptor;
	label: MessageDescriptor;
	value: TLayout;
};

export type LayoutOptionsProps<TLayout> = {
	currentLayoutSelection: PreviewSizeProps<TLayout>['layout'];
	layoutOptions: LayoutOptionsType<TLayout>[];
	PreviewComponent?: PreviewSizeProps<TLayout>['PreviewComponent'];
	previewImage?: (layout: TLayout) => ImageProps;
	source: string;
	updateLayout: (newLayout: TLayout) => void;
	variant?: NonNullable<PreviewSizeVariant>;
};

export const LayoutOptions = <TLayout extends string>({
	updateLayout,
	source,
	layoutOptions,
	variant = 'default',
	PreviewComponent,
	previewImage,
	currentLayoutSelection,
}: LayoutOptionsProps<TLayout>): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const handleClickAnalytics = useCallback(
		(value?: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'selected',
					actionSubject: 'layout',
					actionSubjectId: 'configPanelLayoutOptions',
					source,
					attributes: {
						value,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, source],
	);

	const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedOption = layoutOptions.find(
			(option) => option.value === event.currentTarget.value,
		);
		if (selectedOption) {
			handleClickAnalytics(selectedOption.value);
			updateLayout(selectedOption.value);
		}
	};

	const handleOptionClick = (newLayoutValue: TLayout) => {
		const selectedValue = layoutOptions.find(({ value }) => value === newLayoutValue)?.value;
		handleClickAnalytics(selectedValue);
		updateLayout(newLayoutValue);
	};

	return (
		<>
			{layoutOptions.map(({ value: layout, label: i18nLabel, description: i18nDescription }) => {
				const label = formatMessage(i18nLabel);
				const description = i18nDescription && formatMessage(i18nDescription);
				const { imageSrc, imageSrcDark } = previewImage?.(layout) || {};
				return (
					<Pressable
						xcss={cx(
							layoutOptionStyles.defaultLayoutOption,
							currentLayoutSelection === layout && layoutOptionStyles.layoutOptionSelected,
							layoutOptionStyles[variantMap[variant] as keyof typeof layoutOptionStyles],
						)}
						onClick={() => handleOptionClick(layout)}
						key={`${layout}${label}`}
						testId="layout-option"
					>
						<Flex direction="column" gap="space.100">
							{PreviewComponent && (
								<PreviewSize
									layout={layout}
									previewSizeVariant={variant}
									PreviewComponent={PreviewComponent}
								/>
							)}
							{!PreviewComponent && previewImage && (
								<Image src={imageSrc} srcDark={imageSrcDark} alt="" testId={`${layout}--image`} />
							)}
							<Flex direction="column" gap="space.050">
								<Box xcss={cx(layoutOptionStyles.radioButtonWrapper)}>
									<Radio
										value={layout}
										label={
											<Flex
												alignItems="center"
												xcss={cx(layoutOptionStyles.layoutLabelHeadingWrapper)}
											>
												<Heading size={headingSizeMap[variant]}>{label}</Heading>
											</Flex>
										}
										name={label}
										isChecked={layout === currentLayoutSelection}
										onChange={handleOnChange}
									/>
								</Box>
								{description && (
									<Text align="start" color="color.text.subtlest" size="small">
										{description}
									</Text>
								)}
							</Flex>
						</Flex>
					</Pressable>
				);
			})}
		</>
	);
};
